import { useMemo } from 'react';
import { Info } from 'lucide-react';
import type { WbsItemDoc, WbsItemStatus } from '@mct/shared';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import { sortSiblings } from '../../lib/wbsTree';

/**
 * Rollup explanation + direct-children list rendered inside the
 * "ステータス / 進捗" section of `WbsItemDetailPanel`.
 *
 * Why this exists: phases and any task with children have their
 * status / progress overwritten by `aggregateWbsParent` on every child
 * write, so the panel locks the status `<select>` and the progress
 * `<input>`. Without an explanation users assume the controls are broken.
 * This component (a) names the reason, (b) shows the direct children
 * that drive the rollup so the aggregation is auditable at a glance, and
 * (c) lets the user jump to any child to actually edit the values.
 *
 * Only direct children are listed (grandchildren are surfaced when the
 * user drills into the child) — keeping the section's purpose tight:
 * "what does this rollup aggregate from?". Every direct child is
 * rendered (no truncation); the list is wrapped in a fixed-height
 * scroll container so a phase with hundreds of children stays usable.
 */

const STATUS_LABEL: Record<WbsItemStatus, string> = {
  not_started: '未着手',
  in_progress: '進行中',
  done: '完了',
  blocked: 'ブロック',
  cancelled: '中止',
};

const STATUS_TONE: Record<
  WbsItemStatus,
  'slate' | 'green' | 'yellow' | 'red' | 'blue'
> = {
  not_started: 'slate',
  in_progress: 'blue',
  done: 'green',
  blocked: 'red',
  cancelled: 'slate',
};

/**
 * Pure helper: filter `items` down to the direct children of `parentId`,
 * sorted by `sortKey` using the same segment-aware ja-locale comparator
 * the WBS tree uses. Grandchildren are intentionally excluded — see the
 * component docstring for why.
 *
 * Returns a fresh array (does not mutate `items`).
 */
export function findDirectChildren(
  items: readonly WbsItemDoc[],
  parentId: string,
): WbsItemDoc[] {
  const direct = items.filter((it) => it.parentItemId === parentId);
  return sortSiblings(direct);
}

interface Props {
  parentId: string;
  allItems: readonly WbsItemDoc[];
  /**
   * Optional row-focus callback. Wired to the same `onSelectItem`
   * pattern as `DependencyChips` so clicking a child jumps to (and
   * scrolls) that row in the tree. Omitted callbacks render the row as
   * a non-interactive `<div>`.
   */
  onSelectItem?: (itemId: string) => void;
}

export function RollupChildren({ parentId, allItems, onSelectItem }: Props) {
  const directChildren = useMemo(
    () => findDirectChildren(allItems, parentId),
    [allItems, parentId],
  );

  if (directChildren.length === 0) return null;

  return (
    <div
      className="mt-3 border-t border-slate-200 pt-3"
      data-testid="rollup-children"
    >
      <div className="mb-2 flex items-start gap-1.5 text-[11px] text-slate-500">
        <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" aria-hidden />
        <p>
          このタスクには子タスクがあるため、ステータスと進捗は子タスクから自動計算されます。
          直接編集するには、下の子タスクを編集してください。
        </p>
      </div>
      <div className="mb-1 flex items-baseline justify-between">
        <div className="text-[11px] font-semibold text-slate-600">
          子タスク（直下）
        </div>
        <div className="text-[10px] text-slate-400">
          {directChildren.length} 件
        </div>
      </div>
      <ul
        className="max-h-[240px] space-y-1 overflow-y-auto pr-1"
        data-testid="rollup-children-list"
      >
        {directChildren.map((child) => (
          <ChildRow
            key={child.itemId}
            child={child}
            onSelectItem={onSelectItem}
          />
        ))}
      </ul>
    </div>
  );
}

function ChildRow({
  child,
  onSelectItem,
}: {
  child: WbsItemDoc;
  onSelectItem?: (itemId: string) => void;
}) {
  const statusLabel = STATUS_LABEL[child.status];
  const statusTone = STATUS_TONE[child.status];
  const progress = `${Math.round(child.progress)}%`;

  // Layout: title first (the only label users actually search for),
  // then status badge, then progress on the far right. We used to
  // prefix each row with `child.sortKey` ("1-2-3" style), but the
  // value carries no information here — the WBS tree already shows
  // it on the source row, and `findDirectChildren` already sorts the
  // list by sortKey, so the position in the list is the ordering.
  // Showing the raw key just added visual noise.
  const content = (
    <>
      <span className="min-w-0 flex-1 truncate text-left" title={child.name}>
        {child.name || '(無題)'}
      </span>
      <Badge tone={statusTone} className="shrink-0">
        {statusLabel}
      </Badge>
      <span className="w-9 shrink-0 text-right tabular-nums text-slate-600">
        {progress}
      </span>
    </>
  );

  if (onSelectItem) {
    return (
      <li>
        <button
          type="button"
          onClick={() => onSelectItem(child.itemId)}
          data-rollup-child={child.itemId}
          className={cn(
            'flex w-full items-center gap-2 rounded border border-slate-200 px-2 py-1 text-xs',
            'hover:border-slate-300 hover:bg-slate-50',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400',
          )}
        >
          {content}
        </button>
      </li>
    );
  }
  return (
    <li
      data-rollup-child={child.itemId}
      className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1 text-xs"
    >
      {content}
    </li>
  );
}
